import React, { useState } from "react";
import { Button, IUserLogin, NewUserWithOrganization, useAuth, UserLogin, Users } from "@app/shared";
import { PasswordInput, Input } from "../../../../shared";
import { useModel } from "../../../../../hooks";

interface CreateAccountWizardProps {
    onSuccess: () => void;
    onBackToLoginClick: () => void;
}

export const CreateAccountWizard: React.FC<CreateAccountWizardProps> = ({ onSuccess, onBackToLoginClick }) => {
    const [user, helpers] = useModel(NewUserWithOrganization);
    const auth = useAuth();
    const [isLoading, setIsLoading] = useState(false);

    const handleCreateOnClick = async () => {
        setIsLoading(true);
        try {
            await helpers.validateWithAction(async () => {
                await Users.createUser(user as NewUserWithOrganization);
                try {
                    await auth.logIn(
                        new UserLogin({
                            email: user.emailAddress,
                            password: user.password,
                        } as IUserLogin)
                    );
                    onSuccess();
                } catch (e) {
                    console.error("Error logging in after account creation:", e);
                    onSuccess();
                }
            });
        } catch (error) {
            console.error("Account creation error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full">
            <div className="mb-6 text-center">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Your Account</h2>
                <p className="text-gray-600">Join us to start monitoring with your radar device</p>
            </div>

            <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className={helpers.modelContainerClassNames}>
                    <helpers.ErrorSummary />

                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Personal Details</h3>
                        <div className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input label="First Name" {...helpers.bindingsFor("firstName")} />
                                <Input label="Last Name" {...helpers.bindingsFor("lastName")} />
                            </div>
                            <Input label="Email" {...helpers.bindingsFor("emailAddress")} />
                            <PasswordInput label="Password" {...helpers.bindingsFor("password")} />
                            <PasswordInput label="Confirm Password" {...helpers.bindingsFor("confirmPassword")} />
                        </div>
                    </div>

                    <div className="mb-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Organization Details</h3>
                        <p className="text-sm text-gray-600 mb-4">If your organization already has an account, consider having someone send you an invite.</p>
                        <div className="space-y-4">
                            <Input label="Organization Name" {...helpers.bindingsFor("organizationName")} />
                            <Input label="Phone" {...helpers.bindingsFor("organizationPhoneNumber")} />
                            <Input label="Address" {...helpers.bindingsFor("organizationAddress")} />
                            <Input label="Address Line 2" {...helpers.bindingsFor("organizationAddress2")} />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <Input label="City" {...helpers.bindingsFor("organizationCity")} />
                                <Input label="State" {...helpers.bindingsFor("organizationState")} />
                                <Input label="Zip" {...helpers.bindingsFor("organizationZipcode")} />
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <Button onClick={handleCreateOnClick} className="w-full" disabled={isLoading}>
                            {isLoading ? "Creating Account..." : "Create Account"}
                        </Button>
                        <div className="text-center">
                            <button onClick={onBackToLoginClick} className="text-blue-600 hover:text-blue-700 text-sm">
                                Already have an account? Sign in
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
